export interface CpRoutePermissionRequirement {
  permissions: readonly string[];
  pageLabel: string;
}

function normalizePath(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  if (!path || path === '/') {
    return '/';
  }
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

const CP_ROUTE_PREFIX_RULES: readonly {
  prefix: string;
  requirement: CpRoutePermissionRequirement;
}[] = [
  {
    prefix: '/projects/payment-schedule',
    requirement: {
      permissions: [
        'project_payment_schedule.view',
        'VIEW_MY_PROJECTS',
        'VIEW_PROJECTS',
      ],
      pageLabel: 'Payment Schedule',
    },
  },
  {
    prefix: '/projects',
    requirement: {
      permissions: ['VIEW_PROJECTS', 'VIEW_MY_PROJECTS'],
      pageLabel: 'Projects',
    },
  },
  {
    prefix: '/property-bookings',
    requirement: {
      permissions: ['VIEW_MY_PROJECT_BOOKINGS'],
      pageLabel: 'Bookings',
    },
  },
  {
    prefix: '/price-management',
    requirement: {
      permissions: ['VIEW_CP_PRICE_MANAGEMENT', 'MANAGE_CP_PRICE_MANAGEMENT'],
      pageLabel: 'Price Management',
    },
  },
  {
    prefix: '/rules-and-automations',
    requirement: {
      permissions: [
        'MANAGE_USERS',
        'VIEW_APPROVAL_RULES',
        'VIEW_ASSIGNMENT_RULES',
        'VIEW_SCORING_RULES',
        'VIEW_CP_GENERIC_MASTERS',
      ],
      pageLabel: 'Rules & Automations',
    },
  },
  {
    prefix: '/approval-management',
    requirement: {
      permissions: ['VIEW_APPROVAL_MANAGEMENT', 'MANAGE_USERS'],
      pageLabel: 'Approval Management',
    },
  },
  {
    prefix: '/template-config',
    requirement: {
      permissions: [
        'VIEW_CP_DOCUMENT_TEMPLATES',
        'VIEW_CP_EMAIL_TEMPLATES',
        'VIEW_CP_WHATSAPP_TEMPLATES',
      ],
      pageLabel: 'Templates',
    },
  },
  {
    prefix: '/calendar-events',
    requirement: {
      permissions: ['VIEW_MEETINGS', 'VIEW_LEADS'],
      pageLabel: 'Meetings',
    },
  },
  {
    prefix: '/my-quotations',
    requirement: {
      permissions: ['VIEW_OPPORTUNITIES'],
      pageLabel: 'My Quotations',
    },
  },
  {
    prefix: '/incentive-reports',
    requirement: {
      permissions: ['VIEW_INCENTIVE_REPORTS'],
      pageLabel: 'Incentive Reports',
    },
  },
  {
    prefix: '/system/companies',
    requirement: {
      permissions: ['VIEW_COMPANIES', 'MANAGE_COMPANIES'],
      pageLabel: 'Companies',
    },
  },
  {
    prefix: '/system/audit-logs',
    requirement: {
      permissions: ['VIEW_AUDIT_LOGS'],
      pageLabel: 'Audit Logs',
    },
  },
  {
    prefix: '/system/recycle-bin',
    requirement: {
      permissions: ['VIEW_RECYCLE_BIN'],
      pageLabel: 'Recycle Bin',
    },
  },
  {
    prefix: '/user/internal-users',
    requirement: {
      permissions: ['VIEW_INTERNAL_USERS', 'MANAGE_USERS'],
      pageLabel: 'Internal Users',
    },
  },
  {
    prefix: '/user/roles-permissions',
    requirement: {
      permissions: ['MANAGE_ROLES', 'VIEW_ROLES_PERMISSIONS'],
      pageLabel: 'Roles & Permissions',
    },
  },
  {
    prefix: '/user/departments',
    requirement: {
      permissions: ['VIEW_DEPARTMENTS', 'MANAGE_DEPARTMENTS'],
      pageLabel: 'Departments',
    },
  },
  {
    prefix: '/integrations',
    requirement: {
      permissions: [
        'MANAGE_INTEGRATIONS',
        'VIEW_INTEGRATIONS',
        'VIEW_VENDOR_LOGS',
        'VIEW_VENDOR_CONFIGURATION',
        'VIEW_VENDOR_API',
        'VIEW_VENDOR_API_LOGS',
        'VIEW_SOCIAL_HUB',
        'MANAGE_SOCIAL_HUB',
      ],
      pageLabel: 'Integrations',
    },
  },
  {
    prefix: '/social-hub',
    requirement: {
      permissions: ['VIEW_SOCIAL_HUB', 'MANAGE_SOCIAL_HUB'],
      pageLabel: 'Social Hub',
    },
  },
  {
    prefix: '/bank',
    requirement: {
      permissions: [
        'bank_apf.view',
        'bank_apf.manage',
        'bank_offers.view',
        'bank_offers.manage',
        'bank_fees.view',
        'bank_fees.manage',
      ],
      pageLabel: 'Bank Management',
    },
  },
  {
    prefix: '/settings',
    requirement: {
      permissions: ['VIEW_SETTINGS', 'EDIT_SETTINGS'],
      pageLabel: 'Settings',
    },
  },
  {
    prefix: '/builders',
    requirement: {
      permissions: ['VIEW_BUILDERS'],
      pageLabel: 'Builders',
    },
  },
  {
    prefix: '/campaigns',
    requirement: {
      permissions: ['VIEW_CAMPAIGNS'],
      pageLabel: 'Campaigns',
    },
  },
  {
    prefix: '/people',
    requirement: {
      permissions: ['VIEW_PEOPLE'],
      pageLabel: 'People',
    },
  },
  {
    prefix: '/formiq',
    requirement: {
      permissions: ['VIEW_FORMIQ'],
      pageLabel: 'FormIQ',
    },
  },
  {
    prefix: '/leads',
    requirement: {
      permissions: ['VIEW_LEADS'],
      pageLabel: 'Leads',
    },
  },
  {
    prefix: '/opportunities',
    requirement: {
      permissions: ['VIEW_OPPORTUNITIES'],
      pageLabel: 'Opportunities',
    },
  },
  {
    prefix: '/site-visits',
    requirement: {
      permissions: ['VIEW_SITE_VISITS'],
      pageLabel: 'Site Visits',
    },
  },
  {
    prefix: '/activities',
    requirement: {
      permissions: ['VIEW_LEADS', 'VIEW_OPPORTUNITIES'],
      pageLabel: 'Activities',
    },
  },
  {
    prefix: '/offers',
    requirement: {
      permissions: ['VIEW_OFFERS'],
      pageLabel: 'Offers',
    },
  },
  {
    prefix: '/invoices',
    requirement: {
      permissions: ['VIEW_INVOICES'],
      pageLabel: 'Invoices',
    },
  },
  {
    prefix: '/payments',
    requirement: {
      permissions: ['VIEW_PAYMENTS'],
      pageLabel: 'Payments',
    },
  },
  {
    prefix: '/dashboard',
    requirement: {
      permissions: ['VIEW_DASHBOARD'],
      pageLabel: 'Dashboard',
    },
  },
].sort((a, b) => b.prefix.length - a.prefix.length);

export function isCpPermissionGuardExemptUrl(url: string): boolean {
  const path = normalizePath(url);
  return (
    path === '/access-denied' ||
    path === '/subscription-access-denied' ||
    path === '/dashboard' ||
    path.startsWith('/dashboard/') ||
    path.startsWith('/my-profile')
  );
}

export function resolveCpRoutePermissionRequirement(
  url: string,
): CpRoutePermissionRequirement | null {
  const path = normalizePath(url);
  for (const rule of CP_ROUTE_PREFIX_RULES) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      return rule.requirement;
    }
  }
  return null;
}
